import React from 'react';

import MultiDrawTicketsApi from '../../../Api/CRM/Modules/MultiDrawTicketsApi';
import GetLotteryMultiDrawTicketResponse from '../../../Api/CRM/Responses/LotteryMultiDrawTicketResponse';
import * as formatters from '../../../utils/formatters';
import ActionButton from '../../elements/ActionButton';
import ProtectedLink from '../../elements/ProtectedLink';
import Search from '../../elements/Search';
import BooleanIcon from '../../elements/booleanIcon';
import Card from '../../elements/common/Card';
import FilterByColumn, { Filter } from '../../elements/common/FilterByColumn';
import SortButton from '../../elements/common/SortButton';
import { TableCell as Cell } from '../../elements/common/TableDetailsRow';
import TableHead from '../../elements/common/TableHead';
import TableLoader from '../../elements/common/TableLoader';
import Paginator from '../../elements/paginator';
import useAutoPaginator from '../../hooks/useAutoPaginator';

import { PERMISSION } from 'crm/helpers/Permissions';

function TicketMultiDrawPage() {
    const { items, isLoading, auto } = useAutoPaginator<GetLotteryMultiDrawTicketResponse>((page, params) =>
        MultiDrawTicketsApi.getAll(page, { params })
    );

    return (
        <Card title="All tickets">
            <Search {...auto.search} />
            <FilterByColumn {...auto.filter}>
                <Filter column="last_purchase_date" label="Last draw date" type="dateRange" />
                <Filter column="lottery_multi_draw_tickets.created_at" label="Created at" type="dateRange" />
            </FilterByColumn>
            <TableLoader isLoading={isLoading}>
                <table className="table model-list">
                    <TableHead>
                        <div />
                        <SortButton column="token" {...auto.sort} />
                        <SortButton column="email" label="E-mail" {...auto.sort} />
                        <SortButton column="phone" {...auto.sort} />
                        <SortButton column="lotteries.name" label="Lottery" {...auto.sort} />
                        <SortButton column="tickets" {...auto.sort} />
                        <SortButton column="tickets_left" {...auto.sort} />
                        <SortButton column="ticket_price_net" label="Ticket price" {...auto.sort} />
                        <SortButton column="price_net" label="Price" {...auto.sort} />
                        <SortButton column="is_active" label="Active" {...auto.sort} />
                        <SortButton column="last_purchase_date" {...auto.sort} />
                        <SortButton column="created_at" order="desc" {...auto.sort} />
                    </TableHead>
                    <TicketListTableBody tickets={items} />
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

function TicketListTableBody({ tickets }: { tickets: GetLotteryMultiDrawTicketResponse[] }) {
    const { formatDateStringWithTimezone, formatCurrency } = formatters;

    return (
        <tbody>
            {tickets.map((ticket) => (
                <tr key={ticket.id}>
                    <td>
                        <ActionButton
                            title="Show tickets"
                            url={`/tickets?multidraw_id=${ticket.id}`}
                            permissions={[PERMISSION.VIEW_TICKET]}
                        />
                    </td>
                    <td>{ticket.token}</td>
                    <td>
                        <ProtectedLink
                            url={`/users/${ticket.user_id}`}
                            value={ticket.email}
                            permissions={[PERMISSION.VIEW_USER]}
                        />
                    </td>
                    <Cell>{ticket.phone}</Cell>
                    <td>
                        <ProtectedLink
                            url={`/lotteries/${ticket.lottery_slug}`}
                            value={ticket.lottery_name}
                            permissions={[PERMISSION.VIEW_LOTTERY]}
                        />
                    </td>
                    <Cell>{ticket.tickets}</Cell>
                    <Cell>{ticket.tickets_left}</Cell>
                    <Cell>{formatCurrency(ticket.ticket_price_net)}</Cell>
                    <Cell>{formatCurrency(ticket.price_net)}</Cell>
                    <Cell>
                        <BooleanIcon value={ticket.is_active} />
                    </Cell>
                    <Cell>{formatDateStringWithTimezone(ticket.last_purchase_date, ticket.lottery_timezone)}</Cell>
                    <Cell>{formatDateStringWithTimezone(ticket.created_at)}</Cell>
                </tr>
            ))}
        </tbody>
    );
}

export default TicketMultiDrawPage;
